import * as React from 'react'
import type { EventItem } from '~/client/components/event/EventsDropdown'
import { Button } from '~/client/components/ui/button'
import { Plus, CircleCheck } from 'lucide-react'
import { useEvents } from '~/client/event/use-events.hook'
import { useAddSpaceToEvent } from '~/client/event/use-add-space-to-event.hook'
import { Skeleton } from '~/client/components/ui/skeleton'
import { toast } from 'sonner'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'

type Props = {
  spaceId?: number
  onSelectEvent?: (ev: EventItem) => void
  onCreateEvent?: () => void
}

export function AddToEventStep({ spaceId, onSelectEvent, onCreateEvent }: Props) {
  const [selectedId, setSelectedId] = React.useState<string>('')
  const { data: events, isLoading } = useEvents({ page_size: 200 })
  const addSpaceMutation = useAddSpaceToEvent()

  const items: EventItem[] = React.useMemo(() => {
    if (Array.isArray(events) && events.length > 0) {
      return events.map((e: any) => {
        const firstDate = e?.event_space_list_dates?.[0]?.start_date
        const isSpaceInEvent = spaceId ? e?.space_memberships?.some((m: any) => m.space_id === spaceId) : false
        return {
          id: String(e.id),
          title: e.name,
          dateLabel: formatUtcDateLongFromUnknown(firstDate, 'en-US'),
          isSpaceInEvent,
        }
      })
    }
    return []
  }, [events, spaceId])

  const handleSelect = (ev: EventItem) => {
    setSelectedId(ev.id)
    onSelectEvent?.(ev)

    if (typeof spaceId === 'number') {
      addSpaceMutation
        .mutateAsync({ eventId: Number(ev.id), spaceId })
        .then((result) => {
          if (result.alreadyExists) {
            toast.warning('Space is already in this event', {
              description: ev.title,
              position: 'bottom-right',
            })
          } else {
            toast.success('Space added to event', {
              description: ev.title,
              position: 'bottom-right',
              richColors: true,
            })
          }
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Failed to add space to event'
          toast.error(message, {
            position: 'bottom-right',
            richColors: true,
          })
          // eslint-disable-next-line no-console
          console.error('Failed to add space to event', err)
        })
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[12px] border border-neutral-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] overflow-hidden bg-white max-h-[400px] overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <React.Fragment key={`skeleton-${idx}`}>
              <div className="px-4 py-[18px]">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="mt-2 h-4 w-1/2" />
              </div>
              {idx < 3 ? <div className="h-px bg-[#e4e1e9]" /> : null}
            </React.Fragment>
          ))
        ) : items.length > 0 ? (
          items.map((ev, idx) => {
            const isSelected = selectedId === ev.id
            const isAlreadyAdded = ev.isSpaceInEvent
            return (
              <React.Fragment key={ev.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(ev)}
                  className={`w-full text-left px-4 py-[18px] flex items-center justify-between ${isAlreadyAdded ? 'bg-[rgba(138,61,255,0.02)]' : isSelected ? 'bg-[rgba(138,61,255,0.12)]' : 'bg-white'
                    } hover:bg-[rgba(138,61,255,0.06)]`}
                >
                  <div className="flex-1">
                    <div className="text-[14px] font-medium text-[#55534e] leading-[14px]">{ev.title}</div>
                    {(ev.dateLabel || ev.capacityLabel) && (
                      <div className="mt-1 flex gap-1 text-[14px] font-medium text-[#9b9b9a]">
                        {ev.dateLabel ? <span>{ev.dateLabel}</span> : null}
                        {ev.dateLabel && ev.capacityLabel ? <span>•</span> : null}
                        {ev.capacityLabel ? <span>{ev.capacityLabel}</span> : null}
                      </div>
                    )}
                  </div>
                  {isAlreadyAdded && <CircleCheck className="size-6 text-[#8a3dff] shrink-0" />}
                </button>
                {idx < items.length - 1 ? <div className="h-px bg-[#e4e1e9]" /> : null}
              </React.Fragment>
            )
          })
        ) : (
          <div className="px-4 py-[18px] text-sm text-[#9b9b9a]">No events yet</div>
        )}
      </div>

      <div className="mt-6 mb-3 w-full flex items-center justify-center gap-3">
        <div className="h-px w-24 bg-[#e4e1e9]" />
        <span className="text-[12px] text-black">or</span>
        <div className="h-px w-24 bg-[#e4e1e9]" />
      </div>

      <Button variant="revel" className="w-full h-11 rounded-[12px]" onClick={onCreateEvent}>
        Create Event
        <Plus className="size-4" />
      </Button>
    </div>
  )
}
